import { Component, EventEmitter, inject, NgZone, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { isPlatformBrowser } from '@angular/common';
import { UserService } from "../../services/user.service";
import { CreateTeamModel } from "../../models/user.model";
import { AuthService } from "../../services/auth.service";
import { NgxSmartModalService } from "ngx-smart-modal";
import { CommonService } from "../../services/common.service";
import { TeamService } from "../../services/team.service";
import { catchError, debounceTime, distinctUntilChanged, from, of, Subject, Subscription, switchMap, tap } from "rxjs";
import { SearchDonateToModel } from "../../models/pickup-request.model";
import { ClubService } from "../../services/club.service";
import { GooglePlacesService } from "../../services/google-places.service";
import { AddressModel } from "../../models/address.model";
import PlacesService = google.maps.places.PlacesService;
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../../services/language.service";
import { Router } from "@angular/router";
import { CountryRestrictionService } from "../../services/country-restriction.service";
import { CountryISO, SearchCountryField } from 'ngx-intl-tel-input';

@Component({
  selector: 'app-sign-up-team',
  templateUrl: './sign-up-team.component.html',
  styleUrl: './sign-up-team.component.scss'
})
export class SignUpTeamComponent implements OnInit, OnDestroy {
  private _fb: FormBuilder = inject(FormBuilder);
  private _userService: UserService = inject(UserService);
  private _authService: AuthService = inject(AuthService);
  private _ngxSmartModalService: NgxSmartModalService = inject(NgxSmartModalService);
  private _commonService: CommonService = inject(CommonService);
  private _teamService: TeamService = inject(TeamService);
  private _clubService: ClubService = inject(ClubService);
  private _googlePlacesService: GooglePlacesService = inject(GooglePlacesService);
  private _ngZone: NgZone = inject(NgZone);
  private _translateService: TranslateService = inject(TranslateService);
  private _languageService: LanguageService = inject(LanguageService);
  private _router: Router = inject(Router);
  private _countryRestrictionService: CountryRestrictionService = inject(CountryRestrictionService);
  private _platformId: Object = inject(PLATFORM_ID);

  @Output() stepEmit: EventEmitter<number> = new EventEmitter<number>;

  registrationForm!: FormGroup;
  currentStep: number = 1;
  langSubscription!: Subscription;

  phoneNumber!: string;
  error!: boolean;
  loading!: boolean;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  formError: any;
  studentCouncil: boolean = false;

  typeOptions: string[] = [];
  sportTypes!: string[];
  clubOptions!: any[];
  loadingClubs!: boolean;
  clubSearch$: Subject<string> = new Subject<string>();
  selectedClub!: any;

  addressSearch$: Subject<string> = new Subject<string>();
  selectedAddress!: any;
  loadingAddress!: boolean;
  addressSuggestions: AddressModel[] = [];
  errorMessage!: string | null;
  unsupportedCountryName: string = '';

  isBrowser: boolean = isPlatformBrowser(this._platformId);
  preferredCountries: CountryISO[] = [CountryISO.Norway];
  onlyCountries: CountryISO[] = [];
  selectedCountryISO: CountryISO = CountryISO.Norway;
  readonly searchCountryFields: SearchCountryField[] = [SearchCountryField.Iso2, SearchCountryField.Name];

  constructor() {
    this.formInitialize();
  }

  ngOnInit(): void {
    this.stepEmit.emit(this.currentStep);
    this.getSportTypes();
    this.languageSubscription();
    this.setTypes();
    this.setupPhoneCountryOptions();

    this.clubSearch$
      .pipe(
        tap((): void => {
          this.loadingClubs = true;
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this._clubService.searchClub(query, this.registrationForm.get('sportType')?.value))
      )
      .subscribe({
        next: (result: {query: string, results: SearchDonateToModel[]}) => {
          this.loadingClubs = false;
          this.clubOptions = result.results;
        },
        error: (): void => {
          this.loadingClubs = false;
        }
      });

    this.addressSearch$
      .pipe(
        tap(() => this.loadingAddress = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query =>
          from(this._googlePlacesService.getPlacePredictions(query)).pipe(
            catchError(() => of<google.maps.places.AutocompletePrediction[]>([]))
          )
        )
      )
      .subscribe((results: google.maps.places.AutocompletePrediction[]) => {
        this.loadingAddress = false;
        this.addressSuggestions = results.map((prediction): any => ({
          placeId: prediction.place_id,
          displayName: prediction.description
        }));
      });

    this.sportTypeSubscription();
    this.userTypeSubscription();
  }

  formInitialize(): void {
    this.registrationForm = this._fb.group({
        type: ['', Validators.required],
        code: [''],

        sportType: ['', Validators.required],
        club: [{value: '', disabled: true}, Validators.required],
        name: ['', Validators.required],
        address: ['', Validators.required],
        league: [''],
        ageGroup: ['', [Validators.required, this.ageGroupValidator()]],
        numberOfPlayers: ['', [Validators.required, Validators.min(2)]],
        gender: ['', Validators.required],

        managerName: ['', Validators.required],
        contactPersonName: ['', Validators.required],
        managerAddress: ['', Validators.required],
        phone: [undefined, [Validators.required]],
        email: ['', [Validators.required, Validators.email]],

        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
        emailSubscription: [false],
        confirmTerms: [false, Validators.required]
      },
      { validators: this._commonService.passwordsMatchValidator() });
  }

  languageSubscription(): void {
    this.langSubscription = this._languageService.onLangChange$.subscribe((event: any) => {
      this.setTypes();
    });
  }

  setTypes(): void {
    // 'TEAM_SIGN_UP.STUDENT_COUNCIL'
    this._translateService.get([
      'TEAM_SIGN_UP.TEAM',
    ]).subscribe(translations => {
      this.typeOptions = [translations['TEAM_SIGN_UP.TEAM']];
      // translations['TEAM_SIGN_UP.STUDENT_COUNCIL']
    });

    this.registrationForm.patchValue({
      type: this.typeOptions[0]
    });
  }

  private setupPhoneCountryOptions(): void {
    const allowedCountries = this._countryRestrictionService.getAllowedCountries();
    const mappedCountries = this.mapToCountryIso(allowedCountries);
    const allCountries = this.getAllCountryIsoValues();

    this.onlyCountries = this.uniqueCountries(mappedCountries.length > 1 ? mappedCountries : allCountries);
    const initialCountry = this.onlyCountries.some(code => code === CountryISO.Norway)
      ? CountryISO.Norway
      : (this.onlyCountries[0] || CountryISO.Norway);
    this.selectedCountryISO = initialCountry;
    const preferred = this.uniqueCountries([CountryISO.Norway, ...mappedCountries]);
    this.preferredCountries = this.onlyCountries.length > 1 ? preferred : [];
  }

  private mapToCountryIso(codes: string[]): CountryISO[] {
    const allCountryIsoValues = this.getAllCountryIsoValues();
    const validCountries = new Set<CountryISO>(allCountryIsoValues);

    return codes
      .map(code => (code || '').toLowerCase() as CountryISO)
      .filter(code => validCountries.has(code));
  }

  private uniqueCountries(countries: CountryISO[]): CountryISO[] {
    return Array.from(new Set(countries));
  }

  private getAllCountryIsoValues(): CountryISO[] {
    return Object.keys(CountryISO)
      .map(key => CountryISO[key as keyof typeof CountryISO])
      .filter((value): value is CountryISO => typeof value === 'string');
  }

  onPhoneCountryChange(country: CountryISO | { iso2: string } | null | undefined): void {
    const iso = typeof country === 'string'
      ? country
      : country?.iso2;

    if (!iso) {
      return;
    }

    this.selectedCountryISO = iso.toLowerCase() as CountryISO;
  }

  sportTypeSubscription(): void {
    this.registrationForm.get('sportType')?.valueChanges.subscribe(value => {
      const clubControl = this.registrationForm.get('club');
      if (!value) {
        clubControl?.disable();
      } else {
        clubControl?.enable();
      }
      clubControl?.setValue('');
      this.selectedClub = null;
    });
  }

  userTypeSubscription(): void {
    this.registrationForm.get('type')?.valueChanges.subscribe(value => {
      const sportType = this.registrationForm.get('sportType');
      const league = this.registrationForm.get('league');
      const gender = this.registrationForm.get('gender');

      if (value === 'Team') {
        sportType?.setValue('');

        league?.setValidators(Validators.required);
        league?.updateValueAndValidity();

        gender?.setValidators(Validators.required);
        gender?.updateValueAndValidity();

        this.studentCouncil = false;
      } else if (value === 'Student council') {
        sportType?.setValue('school');
        // league?.setValue(' '); // TODO
        league?.clearValidators();
        league?.updateValueAndValidity();

        gender?.clearValidators();
        gender?.updateValueAndValidity();

        this.studentCouncil = true;
      } else {
        console.log('userTypeSubscription#error user type');
      }
    });
  }

  getSportTypes(): void {
    this._teamService.getSportTypes().subscribe((result) => {
      this.sportTypes = result;
    }, error => {});
  }

  onClubSearch(event: Event): void {
    this.loadingClubs = true;
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value || '';

    if (query.length > 1) {
      this.clubSearch$.next(query);
    } else {
      this.loadingClubs = false;
    }
  }

  onAddressSearch(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value.trim() || '';

    this.selectedAddress = undefined;
    this.clearCountryRestrictionError();

    if (query.length > 1) {
      this.addressSearch$.next(query);
    } else {
      this.addressSuggestions = [];
      this.loadingAddress = false;
    }
  }

  async selectAddress(address: AddressModel): Promise<void> {
    if (!address?.placeId) {
      return;
    }

    try {
      const placesService: PlacesService = new google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails({ placeId: address.placeId }, (place: any, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          return;
        }

        this._ngZone.run(() => {
          const addressComponents = place.address_components || [];
          const findComponent = (types: string[]): any =>
            addressComponents.find((component: any) => {
              const componentTypes: string[] = component?.types || [];
              return types.every(type => componentTypes.indexOf(type) !== -1);
            });

          const getComponentValue = (component: any, variant: 'long' | 'short'): string => {
            if (!component) {
              return '';
            }

            if (variant === 'short') {
              return component.short_name || component.shortText || '';
            }

            return component.long_name || component.longText || '';
          };

          const countryComponent = findComponent(['country']);
          const postalComponent = findComponent(['postal_code']);
          const cityComponent = findComponent(['locality']) || findComponent(['administrative_area_level_2']);
          const stateComponent = findComponent(['administrative_area_level_1']);
          const streetComponent = findComponent(['route']);
          const streetNumberComponent = findComponent(['street_number']);

          const countryCode = getComponentValue(countryComponent, 'short');
          const countryName = getComponentValue(countryComponent, 'long');

          if (!this.isCountryAllowed(countryCode)) {
            this.handleUnsupportedCountry(address.displayName, countryCode, countryName);
            return;
          }

          const location = place.geometry?.location;
          const resolveCoordinate = (coordinate: any, axis: 'lat' | 'lng'): string => {
            if (!coordinate) {
              return '';
            }

            if (typeof coordinate[axis] === 'function') {
              return coordinate[axis]().toString();
            }

            if (typeof coordinate[axis] === 'number') {
              return coordinate[axis].toString();
            }

            return '';
          };

          const lat = resolveCoordinate(location, 'lat');
          const lng = resolveCoordinate(location, 'lng');
          const normalizedCountryCode = countryCode.toUpperCase();

          this.selectedAddress = {
            display_name: address.displayName,
            address: {
              postcode: getComponentValue(postalComponent, 'long') || '',
              city: getComponentValue(cityComponent, 'long') || '',
              state: getComponentValue(stateComponent, 'long') || '',
              country_code: normalizedCountryCode,
              address_line: `${getComponentValue(streetComponent, 'long')} ${getComponentValue(streetNumberComponent, 'long')}`.trim()
            },
            lon: lng ? parseFloat(lng).toString() : '',
            lat: lat ? parseFloat(lat).toString() : ''
          };

          this.clearCountryRestrictionError();
          this.registrationForm.patchValue({ address: address.displayName }, { emitEvent: false });
          this.addressSuggestions = [];
        });
      });
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  }

  private isCountryAllowed(countryCode: string | undefined): boolean {
    return this._countryRestrictionService.isCountryAllowed(countryCode);
  }

  getAllowedCountriesDisplay(): string {
    return this._countryRestrictionService.getAllowedCountriesDisplay();
  }

  private getCountryDisplayName(countryCode: string | undefined): string {
    return this._countryRestrictionService.getCountryDisplayName(countryCode);
  }

  private setCountryRestrictionError(): void {
    const addressControl = this.registrationForm?.get('address');

    if (!addressControl) {
      return;
    }

    const currentErrors = addressControl.errors ?? {};

    addressControl.setErrors({
      ...currentErrors,
      countryNotSupported: true
    });
    addressControl.markAsTouched();
  }

  private clearCountryRestrictionError(): void {
    this.unsupportedCountryName = '';
    const addressControl = this.registrationForm?.get('address');

    if (!addressControl) {
      return;
    }

    const currentErrors: Record<string, any> = { ...(addressControl.errors ?? {}) };

    if (!currentErrors['countryNotSupported']) {
      return;
    }

    delete currentErrors['countryNotSupported'];
    const hasOtherErrors = Object.keys(currentErrors).length > 0;

    addressControl.setErrors(hasOtherErrors ? currentErrors : null);
    addressControl.updateValueAndValidity({ emitEvent: false });
  }

  private handleUnsupportedCountry(displayName: string, countryCode?: string, countryName?: string): void {
    this.selectedAddress = undefined;
    this.unsupportedCountryName = countryName || this.getCountryDisplayName(countryCode) || displayName;
    this.registrationForm.patchValue({ address: displayName }, { emitEvent: false });
    this.setCountryRestrictionError();
    this.addressSuggestions = [];
  }

  goNextStep(): void {
    if (this.currentStep < 4) this.currentStep++;
    this.stepEmit.emit(this.currentStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goPreviousStep(): void {
    if (this.currentStep > 1) this.currentStep--;
    this.stepEmit.emit(this.currentStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!this.registrationForm.get('type')?.valid;
      case 2:
        return !!this.registrationForm.get('sportType')?.valid &&
          !!this.registrationForm.get('club')?.valid &&
          !!this.registrationForm.get('name')?.valid &&
          !!this.registrationForm.get('address')?.valid &&
          !!this.registrationForm.get('ageGroup')?.valid &&
          !!this.registrationForm.get('numberOfPlayers')?.valid &&
          (this.studentCouncil ? true : !!this.registrationForm.get('gender')?.valid) &&
          !!this.selectedAddress;
      case 3:
        return !!this.registrationForm.get('managerName')?.valid &&
          !!this.registrationForm.get('contactPersonName')?.valid &&
          !!this.registrationForm.get('managerAddress')?.valid &&
          !!this.registrationForm.get('phone')?.valid &&
          !!this.registrationForm.get('email')?.valid;
      case 4:
        return !!this.registrationForm.get('password')?.valid &&
          !!this.registrationForm.get('confirmPassword')?.valid &&
          this.registrationForm.get('confirmTerms')?.value === true &&
          !this.registrationForm.hasError('passwordsMismatch');
      default:
        return false;
    }
  }

  selectClubOption(option: any): void {
    this.selectedClub = option;

    this.registrationForm.patchValue({
      club: option.name
    });
    this.clubOptions = []; // Clear options after selection
  }

  handleValidationErrors(error: any): void {
    const errorResponse = error?.error;
    
    // Handle different error formats
    if (errorResponse?.detail) {
      const detail = errorResponse.detail;
      
      // Check if it's a string format like "profile: ['phone_number']"
      if (typeof detail === 'string' && detail.includes('phone_number')) {
        this.formError = {
          profile: {
            phone_number: [this._translateService.instant('TEAM_SIGN_UP.ERRORS.PHONE_EXISTS')]
          }
        };
        this.currentStep = 3; // Navigate to step 3 where phone is located
        this.stepEmit.emit(this.currentStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      // Try to parse as JSON-like string
      try {
        const jsonReadyStr: string = `{${detail.replace(/^(\w+):/, '"$1":').replace(/'/g, '"')}}`;
        this.formError = JSON.parse(jsonReadyStr);
      } catch (e) {
        this.errorMessage = detail;
        this.error = true;
      }
    } else if (errorResponse?.email) {
      // Direct field errors
      this.formError = errorResponse;
    } else if (errorResponse?.profile) {
      this.formError = errorResponse;
    } else {
      this.errorMessage = this._translateService.instant('TEAM_SIGN_UP.ERRORS.GENERIC');
      this.error = true;
    }
  }

  onSubmit(): void {
    if (this.loading) return;

    this.error = false;
    this.loading = true;
    this.errorMessage = null;
    this.formError = null;

    if (!this.registrationForm.valid) return;

    const formData: any = this.registrationForm.value;
    const normalizedPhone: string = this.extractE164Phone(formData.phone);
    if (!normalizedPhone) {
      this.loading = false;
      const phoneControl = this.registrationForm.get('phone');
      phoneControl?.setErrors({ validatePhoneNumber: true });
      phoneControl?.markAsTouched();
      return;
    }

    // Normalize ageGroup to "nn-nn" format (e.g., "9-13" -> "09-13")
    const ageGroupMatch = formData.ageGroup.match(/^(\d{1,2})-(\d{1,2})$/);
    if (!ageGroupMatch) {
      this.loading = false;
      return;
    }
    const start: number = parseInt(ageGroupMatch[1], 10);
    const end: number = parseInt(ageGroupMatch[2], 10);

    let associatedClub: any = {};
    if (this.selectedClub) {
      associatedClub.id = this.selectedClub.id;
    } else {
      associatedClub.name = formData.club;
    }

    const phoneCountryCode: string = (this.selectedCountryISO || CountryISO.Norway).toUpperCase();

    const userData: CreateTeamModel = {
      email: formData.email as string,
      password: formData.password as string,
      profile: {
        name: formData.name as string,
        manager_name: formData.managerName as string,
        manager_address: formData.managerAddress as string,
        contact_person_name: formData.contactPersonName as string,
        phone_number: normalizedPhone,
        league: formData.league as string,
        age_group: [start, end],
        number_of_players: formData.numberOfPlayers,
        address_line: this.selectedAddress.address.address_line || '',
        postal_code: this.selectedAddress.address.postcode || '',
        city: this.selectedAddress.address.city || this.selectedAddress.address.town || '',
        state: this.selectedAddress.address.state || '',
        country: this.selectedAddress.address.country_code.toUpperCase(),
        country_code: phoneCountryCode,
        sport_type: formData.sportType as string,
        gender: formData.gender as string,
        associated_club: associatedClub,
        email_subscription: !!formData.emailSubscription
      }
    };

    if (formData.type === this.typeOptions[1]) {
      userData.profile.school = true;
    }

    this._userService.createTeam(userData, formData.code).subscribe(result => {
      const accessToken: string = result.tokens.access;
      this._authService.setAccessToken(accessToken);

      this._userService.set(result);

      this.phoneNumber = normalizedPhone;
      setTimeout(() => {
        this._ngxSmartModalService.getModal('otpModal').open();
      }, 150);
    }, error => {
      this.loading = false;

      if (error && error.status === 400) {
        this.handleValidationErrors(error);
      } else {
        const details = error?.error?.detail;
        this.errorMessage = details || this._translateService.instant('TEAM_SIGN_UP.ERRORS.GENERIC');
        this.error = true;
      }
    });
  }

  private extractE164Phone(phoneValue: any): string {
    if (!phoneValue) {
      return '';
    }

    if (typeof phoneValue === 'object') {
      if (phoneValue.countryCode) {
        this.selectedCountryISO = (phoneValue.countryCode as string).toLowerCase() as CountryISO;
      }

      if (phoneValue.e164Number) {
        return phoneValue.e164Number;
      }

      if (phoneValue.internationalNumber) {
        return this.normalizePhoneNumber(phoneValue.internationalNumber);
      }

      if (phoneValue.number) {
        return this.normalizePhoneNumber(phoneValue.number);
      }
    }

    if (typeof phoneValue === 'string') {
      return this.normalizePhoneNumber(phoneValue);
    }

    return '';
  }

  private normalizePhoneNumber(phone: string): string {
    if (!phone) {
      return '';
    }

    const digitsOnly = phone.replace(/[^0-9+]/g, '').replace(/^\++/, '');
    if (!digitsOnly) {
      return '';
    }

    return `+${digitsOnly}`;
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword') {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else if (field === 'confirmPassword') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  ageGroupValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;

      if (!value) return { invalidFormat: true };

      // Accept formats like "9-13", "09-13", "9-3", "09-3", etc.
      const match = value.match(/^(\d{1,2})-(\d{1,2})$/);
      if (!match) return { invalidFormat: true };

      const start: number = parseInt(match[1], 10);
      const end: number = parseInt(match[2], 10);

      if (start > end) return { invalidRange: true };

      return null;
    };
  }

  objectKeys(obj: object): string[] {
    return Object.keys(obj);
  }

  goToTermsConditions(): void {
    const currentLang: string = this._translateService.currentLang;
    const url = `/${currentLang}/henter-pant/terms-and-conditions`;
    window.open(url, '_blank');
  }

  goToPrivacyPolicy(): void {
    const currentLang: string = this._translateService.currentLang;
    const url = `/${currentLang}/henter-pant/privacy-policy`;
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    if (this.langSubscription) this.langSubscription.unsubscribe();
  }
}
